import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "../../styles/auth.css";
import logoPng from "../../assets/images/logo.png";
import { toast } from "react-toastify";
import http from "../../lib/http";
import { setGuestToken, clearUser } from "../../utils/authHelpers";

// ── Icons ─────────────────────────────────────────────
const EyeIcon = ({ open }) =>
  open ? (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
const ArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const GuestIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const MailIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);
const LockIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ── Component ──────────────────────────────────────────
export default function Login() {
  const navigate = useNavigate();
  const { login, googleLogin } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const googleInitRef = useRef(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success("Welcome back 👋");
      navigate("/home");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Login failed. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleContinueAsGuest = async () => {
    try {
      setGuestLoading(true);
      setError("");
      clearUser();
      const res = await http.post("/api/guest/token");
      const data = res?.data;
      if (!data?.success || !data?.guest_token) {
        toast.error("Could not start guest session");
        return;
      }
      setGuestToken(data.guest_token);
      toast.success("Continuing as guest 👤");
      navigate("/home");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Guest session failed.");
    } finally {
      setGuestLoading(false);
    }
  };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;
    const initGoogle = () => {
      if (googleInitRef.current || !window.google?.accounts?.id) return;
      googleInitRef.current = true;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          const idToken = response?.credential;
          if (!idToken) {
            toast.error("Google authentication failed");
            return;
          }
          setGoogleLoading(true);
          setError("");
          const ok = await googleLogin(idToken);
          if (!ok) {
            setGoogleLoading(false);
            toast.error("Google login failed");
            return;
          }
          localStorage.removeItem("guestToken");
          toast.success("Logged in with Google 🚀");
          navigate("/home");
        },
      });
      const el = document.getElementById("googleSignInDiv");
      if (el)
        window.google.accounts.id.renderButton(el, {
          theme: "outline",
          size: "large",
          text: "signin_with",
          width: 320,
        });
    };
    initGoogle();
    const t = setTimeout(initGoogle, 300);
    return () => clearTimeout(t);
  }, [googleLogin, navigate]);

  const anyBusy = loading || googleLoading || guestLoading;

  return (
    <div className="auth-page">
      {/* Loading overlay */}
      {(googleLoading || guestLoading) && (
        <div className="auth-overlay">
          <div className="auth-overlay-card">
            <div className="auth-overlay-dots">
              <span />
              <span />
              <span />
              <span />
            </div>
            <div className="auth-overlay-title">
              {guestLoading ? "Starting guest session" : "Signing you in"}
            </div>
            <div className="auth-overlay-sub">
              {guestLoading
                ? "Preparing your browsing experience…"
                : "Securely connecting with Google…"}
            </div>
          </div>
        </div>
      )}

      {/* ══ LEFT PANEL ══ */}
      <div className="auth-left">
        <div className="auth-left-content">
          {/* Big crisp logo */}
          <div className="auth-left-logo auth-left-logo-xl">
            <img src={logoPng} alt="Easy World Online" />
          </div>

          <h1 className="auth-left-title">
            Shop the world,
            <br />
            delivered to you.
          </h1>
          <p className="auth-left-sub">
            Thousands of products. Fast delivery. Trusted vendors.
          </p>

          <div className="auth-left-pills">
            <span className="auth-pill">🚀 Fast Delivery</span>
            <span className="auth-pill">🛡️ Secure Checkout</span>
            <span className="auth-pill">⭐ Trusted Sellers</span>
          </div>

          {/* App store section */}
          <div className="auth-app-section">
            <p className="auth-app-label">Also available on mobile</p>
            <div className="auth-store-btns">
              <a
                href="https://apps.apple.com/us/app/easy-world-online-shopping-app/id6756601701"
                target="_blank"
                rel="noopener noreferrer"
                className="auth-store-btn"
              >
                <span className="auth-store-icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                </span>
                <span className="auth-store-text">
                  <span className="auth-store-sub">Download on the</span>
                  <span className="auth-store-name">App Store</span>
                </span>
              </a>

              <a
                href="https://play.google.com/store/apps/details?id=com.ableinnovation.easy_world_customer"
                target="_blank"
                rel="noopener noreferrer"
                className="auth-store-btn"
              >
                <span className="auth-store-icon">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M3.18 23.76c.3.17.64.22.98.14l11.5-11.5-2.44-2.45-10.04 13.81zm16.3-10.49-2.88-1.67-2.63 2.63 2.87 2.87c.52.52 1.36.52 1.88 0l.76-.76c.52-.52.52-1.36 0-1.87zM3.54.14A1.33 1.33 0 0 0 2 1.41v21.18c0 .58.35 1.1.88 1.33l.24.1 11.87-11.87v-.28L3.54.14zm13.6 8.27L13.77 5.1 2.28.14 14.9 12.77l2.24-4.36z" />
                  </svg>
                </span>
                <span className="auth-store-text">
                  <span className="auth-store-sub">Get it on</span>
                  <span className="auth-store-name">Google Play</span>
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />
        <div className="auth-orb auth-orb-3" />
      </div>

      {/* ══ RIGHT PANEL ══ */}
      <div className="auth-right">
        <div className="auth-card">
          <div className="auth-card-header">
            <div className="auth-card-logo">
              <img src={logoPng} alt="Easy World" />
            </div>
            <div>
              <h2 className="auth-card-title">Welcome back</h2>
              <p className="auth-card-sub">
                Sign in to your account to continue
              </p>
            </div>
          </div>

          {error && (
            <div className="auth-error">
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="auth-field">
              <label className="auth-label">Email Address</label>
              <div className="auth-input-wrap">
                <span className="auth-input-icon">
                  <MailIcon />
                </span>
                <input
                  type="email"
                  name="email"
                  className="auth-input"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={anyBusy}
                  required
                />
              </div>
            </div>

            <div className="auth-field">
              <div className="auth-label-row">
                <label className="auth-label">Password</label>
                <Link to="/forgot-password" className="auth-forgot">
                  Forgot password?
                </Link>
              </div>
              <div className="auth-input-wrap">
                <span className="auth-input-icon">
                  <LockIcon />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  className="auth-input auth-input-password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={anyBusy}
                  required
                />
                <button
                  type="button"
                  className="auth-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={anyBusy}
                  tabIndex={-1}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={anyBusy}
            >
              {loading ? (
                <>
                  <span className="auth-btn-spinner" />
                  &nbsp;Logging in…
                </>
              ) : (
                <>
                  Login &nbsp;
                  <ArrowIcon />
                </>
              )}
            </button>
          </form>

          <div className="auth-divider">
            <span>or continue with</span>
          </div>

          {!googleLoading && !guestLoading && (
            <div className="auth-google-wrap">
              <div id="googleSignInDiv" />
            </div>
          )}

          <button
            type="button"
            className="auth-btn auth-btn-ghost"
            onClick={handleContinueAsGuest}
            disabled={anyBusy}
          >
            {guestLoading ? (
              <>
                <span className="auth-btn-spinner auth-btn-spinner-dark" />
                &nbsp;Starting session…
              </>
            ) : (
              <>
                <GuestIcon />
                &nbsp;Continue as Guest
              </>
            )}
          </button>

          <p className="auth-footer-text">
            Don't have an account?{" "}
            <Link to="/signup" className="auth-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
